import { inject, Injectable } from '@angular/core';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { PersistedSort } from '../models/saved-view';
import { FILTERS_STORAGE_SUFFIX, SORT_STORAGE_SUFFIX, VIEW_ID_STORAGE_SUFFIX } from '../constants/view-persistence';

@Injectable({ providedIn: 'root' })
export class ViewPersistenceService {
  private userDataService = inject(UserDataService);

  getStorageKey(tableName: string): string {
    return `${tableName}_${this.userDataService.user.id}_${this.userDataService.company.id}`;
  }

  loadFilters(tableName: string): Record<string, unknown> | null {
    try {
      const raw = localStorage.getItem(this.filtersKey(tableName));
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;

      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  saveFilters(tableName: string, formValue: Record<string, unknown>): void {
    localStorage.setItem(this.filtersKey(tableName), JSON.stringify(formValue));
  }

  clearFilters(tableName: string): void {
    localStorage.removeItem(this.filtersKey(tableName));
  }

  loadSelectedViewId(tableName: string): number | null {
    const raw = localStorage.getItem(this.viewIdKey(tableName));
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? null : parsed;
  }

  saveSelectedViewId(tableName: string, viewId: number): void {
    localStorage.setItem(this.viewIdKey(tableName), String(viewId));
  }

  clearSelectedViewId(tableName: string): void {
    localStorage.removeItem(this.viewIdKey(tableName));
  }

  loadSort(tableName: string): PersistedSort | null {
    try {
      const raw = localStorage.getItem(this.sortKey(tableName));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return {
        sortBy: typeof parsed.sortBy === 'string' ? parsed.sortBy : '',
        sortDirection: parsed.sortDirection === 'asc' || parsed.sortDirection === 'desc' ? parsed.sortDirection : '',
      };
    } catch {
      return null;
    }
  }

  saveSort(tableName: string, sort: PersistedSort): void {
    localStorage.setItem(this.sortKey(tableName), JSON.stringify(sort));
  }

  clearSort(tableName: string): void {
    localStorage.removeItem(this.sortKey(tableName));
  }

  private filtersKey(tableName: string): string {
    return `${this.getStorageKey(tableName)}${FILTERS_STORAGE_SUFFIX}`;
  }

  private viewIdKey(tableName: string): string {
    return `${this.getStorageKey(tableName)}${VIEW_ID_STORAGE_SUFFIX}`;
  }

  private sortKey(tableName: string): string {
    return `${this.getStorageKey(tableName)}${SORT_STORAGE_SUFFIX}`;
  }
}
